import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    buildDisplayText,
    buildHighlightedHtmlFromMatches,
    buildHighlightedHtmlWithCollapsedPlaceholders,
    findFoldRegions,
    findSearchMatches,
    getCollapsedLinesWithLineExpanded,
    getCollapsedPlaceholderAtPosition,
    getCollapsedPlaceholderRanges,
    getLineForPosition,
    mapFullPositionToDisplay,
    mapSearchMatchesToDisplay,
    normalizeCollapsedSelection,
    stringifyConfig,
} from "./screenConfigJsonEditorUtils";

interface ScreenConfigJsonEditorProps {
    isOpen: boolean;
    onClose: () => void;
    config: unknown;
    screenId: string;
    source?: string;
}

const EDITOR_SURFACE_CLASS = "screen-config-json-wrap whitespace-pre-wrap break-words";

const EditIcon: React.FC = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        fill="currentColor"
        className="w-3.5 h-3.5 shrink-0"
        aria-hidden="true"
    >
        <path d="M12.146.146a.5.5 0 0 1 .708 0l2.25 2.25a.5.5 0 0 1 0 .708l-9.5 9.5a.5.5 0 0 1-.168.11l-4 1.5a.5.5 0 0 1-.65-.65l1.5-4a.5.5 0 0 1 .11-.168l9.5-9.5z" />
    </svg>
);

export { EditIcon };

const toolbarButtonClass =
    "rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700";

const toolbarButtonActiveClass =
    "rounded-lg border border-sky-500 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700 dark:border-sky-500 dark:bg-sky-950/40 dark:text-sky-200";

export const ScreenConfigJsonEditor: React.FC<ScreenConfigJsonEditorProps> = ({
    isOpen,
    onClose,
    config,
    screenId,
    source,
}) => {
    const serializedConfig = useMemo(() => stringifyConfig(config), [config]);
    const [value, setValue] = useState(serializedConfig);
    const [copied, setCopied] = useState(false);
    const [collapsedLines, setCollapsedLines] = useState<Set<number>>(new Set());
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchMatchIndex, setSearchMatchIndex] = useState(0);
    const [matchCase, setMatchCase] = useState(false);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const highlightRef = useRef<HTMLPreElement>(null);
    const lineNumbersRef = useRef<HTMLDivElement>(null);
    const foldGutterRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const foldRegions = useMemo(() => findFoldRegions(value), [value]);
    const foldRegionByStartLine = useMemo(
        () => new Map(foldRegions.map((region) => [region.startLine, region])),
        [foldRegions]
    );
    const isCollapsedView = collapsedLines.size > 0;
    const displayValue = useMemo(
        () => buildDisplayText(value, collapsedLines, foldRegions),
        [value, collapsedLines, foldRegions]
    );
    const searchMatches = useMemo(
        () => findSearchMatches(value, searchQuery, matchCase),
        [value, searchQuery, matchCase]
    );
    const displaySearchMatches = useMemo(
        () => mapSearchMatchesToDisplay(value, collapsedLines, foldRegions, searchMatches),
        [value, collapsedLines, foldRegions, searchMatches]
    );
    const collapsedPlaceholderRanges = useMemo(
        () => getCollapsedPlaceholderRanges(value, collapsedLines, foldRegions),
        [value, collapsedLines, foldRegions]
    );
    const highlighted = useMemo(() => {
        if (isCollapsedView) {
            return buildHighlightedHtmlWithCollapsedPlaceholders(
                displayValue,
                collapsedPlaceholderRanges,
                displaySearchMatches,
                searchMatchIndex
            );
        }

        return buildHighlightedHtmlFromMatches(
            displayValue,
            displaySearchMatches,
            searchMatchIndex
        );
    }, [
        displayValue,
        isCollapsedView,
        collapsedPlaceholderRanges,
        displaySearchMatches,
        searchMatchIndex,
    ]);
    const displayLineNumbers = useMemo(() => {
        if (!isCollapsedView) {
            return displayValue.split("\n").map((_, index) => index + 1);
        }

        const hiddenLines = new Set<number>();
        foldRegions.forEach((region) => {
            if (!collapsedLines.has(region.startLine)) return;
            for (let line = region.startLine + 1; line <= region.endLine; line += 1) {
                hiddenLines.add(line);
            }
        });

        return value
            .split("\n")
            .map((_, index) => index + 1)
            .filter((lineNumber) => !hiddenLines.has(lineNumber));
    }, [value, displayValue, isCollapsedView, collapsedLines, foldRegions]);

    const parseError = useMemo(() => {
        try {
            JSON.parse(value);
            return null;
        } catch (error) {
            return error instanceof Error ? error.message : "Invalid JSON";
        }
    }, [value]);

    useEffect(() => {
        if (isOpen) {
            setValue(serializedConfig);
            setCopied(false);
            setCollapsedLines(new Set());
            setSearchOpen(false);
            setSearchQuery("");
            setSearchMatchIndex(0);
            window.setTimeout(() => textareaRef.current?.focus(), 0);
        }
    }, [isOpen, serializedConfig]);

    useEffect(() => {
        if (searchMatches.length === 0) {
            setSearchMatchIndex(0);
            return;
        }
        if (searchMatchIndex >= searchMatches.length) {
            setSearchMatchIndex(0);
        }
    }, [searchMatches.length, searchMatchIndex]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "f") {
                event.preventDefault();
                setSearchOpen(true);
                window.setTimeout(() => searchInputRef.current?.focus(), 0);
                return;
            }

            if (event.key === "Escape") {
                if (searchOpen) {
                    setSearchOpen(false);
                    setSearchQuery("");
                    return;
                }
                onClose();
            }
        };

        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, onClose, searchOpen]);

    const syncScroll = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const scrollTop = textarea.scrollTop;
        const scrollLeft = textarea.scrollLeft;

        if (highlightRef.current) {
            highlightRef.current.scrollTop = scrollTop;
            highlightRef.current.scrollLeft = scrollLeft;
        }
        if (lineNumbersRef.current) {
            lineNumbersRef.current.scrollTop = scrollTop;
        }
        if (foldGutterRef.current) {
            foldGutterRef.current.scrollTop = scrollTop;
        }
    }, []);

    const scrollToSearchMatch = useCallback(
        (matchIndex: number, collapsedOverride?: Set<number>) => {
            const match = searchMatches[matchIndex];
            const textarea = textareaRef.current;
            if (!match || !textarea) return;

            const matchLine = getLineForPosition(value, match.start);
            const nextCollapsed =
                collapsedOverride ??
                getCollapsedLinesWithLineExpanded(collapsedLines, foldRegions, matchLine);

            const collapsedUnchanged =
                nextCollapsed.size === collapsedLines.size &&
                [...nextCollapsed].every((line) => collapsedLines.has(line));

            if (!collapsedUnchanged) {
                setCollapsedLines(nextCollapsed);
            }

            const displayStart = mapFullPositionToDisplay(
                value,
                nextCollapsed,
                foldRegions,
                match.start
            );
            const displayEnd = mapFullPositionToDisplay(
                value,
                nextCollapsed,
                foldRegions,
                match.end
            );

            window.requestAnimationFrame(() => {
                textarea.focus();
                textarea.setSelectionRange(displayStart, Math.max(displayStart, displayEnd));
                syncScroll();
            });
        },
        [searchMatches, value, collapsedLines, foldRegions, syncScroll]
    );

    const goToSearchMatch = useCallback(
        (direction: 1 | -1) => {
            if (searchMatches.length === 0) return;

            const nextIndex =
                (searchMatchIndex + direction + searchMatches.length) %
                searchMatches.length;
            const match = searchMatches[nextIndex];
            const matchLine = getLineForPosition(value, match.start);
            const nextCollapsed = getCollapsedLinesWithLineExpanded(
                collapsedLines,
                foldRegions,
                matchLine
            );

            setSearchMatchIndex(nextIndex);
            scrollToSearchMatch(nextIndex, nextCollapsed);
        },
        [searchMatchIndex, searchMatches, value, collapsedLines, foldRegions, scrollToSearchMatch]
    );

    useEffect(() => {
        if (!searchQuery || searchMatches.length === 0) return;
        scrollToSearchMatch(0);
    }, [searchQuery, matchCase, searchMatches.length, scrollToSearchMatch]);

    const toggleFold = (startLine: number) => {
        setCollapsedLines((previous) => {
            const next = new Set(previous);
            if (next.has(startLine)) {
                next.delete(startLine);
            } else {
                next.add(startLine);
            }
            return next;
        });
    };

    const collapseAll = () => {
        setCollapsedLines(new Set(foldRegions.map((region) => region.startLine)));
    };

    const expandAll = () => {
        setCollapsedLines(new Set());
    };

    const handleFormat = () => {
        try {
            const parsed = JSON.parse(value);
            setValue(JSON.stringify(parsed, null, 2));
            setCollapsedLines(new Set());
        } catch {
            // parseError already shown
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            setCopied(false);
        }
    };

    const applyCollapsedSelection = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea || !isCollapsedView || collapsedPlaceholderRanges.length === 0) {
            return;
        }

        const normalized = normalizeCollapsedSelection(
            textarea.selectionStart,
            textarea.selectionEnd,
            collapsedPlaceholderRanges
        );

        if (
            normalized.start !== textarea.selectionStart ||
            normalized.end !== textarea.selectionEnd
        ) {
            textarea.setSelectionRange(normalized.start, normalized.end);
        }
    }, [isCollapsedView, collapsedPlaceholderRanges]);

    const handleEditorSelect = () => {
        window.requestAnimationFrame(applyCollapsedSelection);
    };

    const handleEditorMouseUp = () => {
        window.requestAnimationFrame(applyCollapsedSelection);
    };

    const handleEditorDoubleClick = () => {
        const textarea = textareaRef.current;
        if (!textarea || !isCollapsedView) return;

        const placeholder = getCollapsedPlaceholderAtPosition(
            textarea.selectionStart,
            collapsedPlaceholderRanges
        );

        if (placeholder) {
            textarea.setSelectionRange(placeholder.start, placeholder.end);
        }
    };

    const handleEditorCopy = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const { selectionStart, selectionEnd } = textarea;
        const hasSelection = selectionStart !== selectionEnd;

        event.preventDefault();

        if (hasSelection) {
            void navigator.clipboard.writeText(
                displayValue.slice(selectionStart, selectionEnd)
            );
            return;
        }

        void navigator.clipboard.writeText(value);
    };

    const handleDisplayChange = (nextDisplayValue: string) => {
        if (isCollapsedView) return;
        setValue(nextDisplayValue);
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[100006] flex items-center justify-center p-4 sm:p-6">
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                onClick={onClose}
                aria-hidden="true"
            />

            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="screen-config-editor-title"
                className="relative flex w-full max-w-5xl max-h-[92vh] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
            >
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
                    <div>
                        <h2
                            id="screen-config-editor-title"
                            className="text-lg font-semibold text-slate-900 dark:text-slate-100"
                        >
                            Screen configuration
                        </h2>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            Screen {screenId}
                            {source ? (
                                <>
                                    {" "}
                                    ·{" "}
                                    <span className="font-medium text-slate-700 dark:text-slate-300">
                                        {source}
                                    </span>
                                </>
                            ) : null}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                        aria-label="Close editor"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-5 py-3 dark:border-slate-700">
                    <button type="button" onClick={handleFormat} className={toolbarButtonClass}>
                        Format
                    </button>
                    <button type="button" onClick={handleCopy} className={toolbarButtonClass}>
                        {copied ? "Copied" : "Copy"}
                    </button>
                    <button
                        type="button"
                        onClick={collapseAll}
                        className={toolbarButtonClass}
                        title="Collapse all objects and arrays"
                    >
                        Collapse all
                    </button>
                    <button
                        type="button"
                        onClick={expandAll}
                        className={toolbarButtonClass}
                        title="Expand all folded sections"
                    >
                        Expand all
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setSearchOpen((previous) => !previous);
                            window.setTimeout(() => searchInputRef.current?.focus(), 0);
                        }}
                        className={searchOpen ? toolbarButtonActiveClass : toolbarButtonClass}
                        title="Search (Ctrl+F)"
                    >
                        Search
                    </button>
                    <span
                        className={`ml-auto rounded-full px-2.5 py-1 text-xs font-medium ${
                            parseError
                                ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                                : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                        }`}
                    >
                        {parseError ? "Invalid JSON" : "Valid JSON"}
                    </span>
                </div>

                {searchOpen && (
                    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-5 py-2 dark:border-slate-700 dark:bg-slate-950/40">
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(event) => {
                                setSearchQuery(event.target.value);
                                setSearchMatchIndex(0);
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    goToSearchMatch(event.shiftKey ? -1 : 1);
                                }
                            }}
                            placeholder="Find in JSON..."
                            className="min-w-[220px] flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 focus:border-sky-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                        />
                        <button
                            type="button"
                            onClick={() => goToSearchMatch(-1)}
                            className={toolbarButtonClass}
                            disabled={searchMatches.length === 0}
                        >
                            Prev
                        </button>
                        <button
                            type="button"
                            onClick={() => goToSearchMatch(1)}
                            className={toolbarButtonClass}
                            disabled={searchMatches.length === 0}
                        >
                            Next
                        </button>
                        <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                            <input
                                type="checkbox"
                                checked={matchCase}
                                onChange={(event) => setMatchCase(event.target.checked)}
                            />
                            Match case
                        </label>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                            {searchQuery
                                ? searchMatches.length > 0
                                    ? `${searchMatchIndex + 1} of ${searchMatches.length}`
                                    : "No matches"
                                : "Type to search"}
                        </span>
                    </div>
                )}

                <div className="screen-config-json-editor flex min-h-[360px] h-[65vh] overflow-hidden">
                    <div
                        ref={foldGutterRef}
                        className="screen-config-json-foldgutter select-none overflow-hidden border-r border-[#2d2d2d] py-3"
                        aria-hidden="true"
                    >
                        {displayLineNumbers.map((lineNumber) => {
                            const region = foldRegionByStartLine.get(lineNumber);
                            const isCollapsed = collapsedLines.has(lineNumber);

                            return (
                                <div
                                    key={`fold-${lineNumber}`}
                                    className="flex h-7 items-center justify-center"
                                >
                                    {region ? (
                                        <button
                                            type="button"
                                            onClick={() => toggleFold(lineNumber)}
                                            className="flex h-5 w-5 items-center justify-center rounded text-sm leading-none text-[#cccccc] hover:bg-[#2d2d2d] hover:text-white"
                                            title={isCollapsed ? "Expand section" : "Collapse section"}
                                            aria-label={isCollapsed ? "Expand section" : "Collapse section"}
                                        >
                                            {isCollapsed ? "▸" : "▾"}
                                        </button>
                                    ) : (
                                        <span className="inline-block h-5 w-5" />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div
                        ref={lineNumbersRef}
                        className="screen-config-json-linenumbers select-none overflow-hidden border-r border-[#2d2d2d] px-3 py-3 text-right font-mono text-sm leading-7"
                        aria-hidden="true"
                    >
                        {displayLineNumbers.map((lineNumber) => (
                            <div key={`line-${lineNumber}`}>{lineNumber}</div>
                        ))}
                    </div>

                    <div className="relative min-h-0 min-w-0 flex-1">
                        <pre
                            ref={highlightRef}
                            className={`screen-config-json-highlight pointer-events-none absolute inset-0 overflow-hidden px-4 py-3 font-mono text-base leading-7 ${EDITOR_SURFACE_CLASS}`}
                            aria-hidden="true"
                            dangerouslySetInnerHTML={{ __html: `${highlighted}\n` }}
                        />
                        <textarea
                            ref={textareaRef}
                            value={displayValue}
                            onChange={(event) => handleDisplayChange(event.target.value)}
                            onCopy={handleEditorCopy}
                            onSelect={handleEditorSelect}
                            onMouseUp={handleEditorMouseUp}
                            onDoubleClick={handleEditorDoubleClick}
                            onScroll={syncScroll}
                            readOnly={isCollapsedView}
                            spellCheck={false}
                            className={`screen-config-json-textarea absolute inset-0 resize-none overflow-auto border-0 px-4 py-3 font-mono text-base leading-7 focus:outline-none focus:ring-0 ${EDITOR_SURFACE_CLASS}`}
                            aria-label="Screen configuration JSON"
                        />
                    </div>
                </div>

                {parseError ? (
                    <div className="border-t border-red-200 bg-red-50 px-5 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
                        {parseError}
                    </div>
                ) : (
                    <div className="border-t border-slate-200 px-5 py-2 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                        {isCollapsedView
                            ? "Collapsed sections select as { ... } / [ ... ] only. Copy button still copies full JSON."
                            : "Development tool — edit, format, fold, search, or copy this configuration."}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};
